import { scrollTo } from '@helpers/animate';
import { API_URLS } from '@helpers/api';
import SvgClipPaths from '@helpers/svgClipPaths';
import { getSvgFromSystem, SystemIcon } from '@helpers/systemIcons';
import { useApi } from '@hooks/useApi';
import { useForm, validateNotWhitespaceOnly } from '@hooks/useForm';
import SvgLogo from '@img/primary-logo.svg';
import { LinkButton } from '@stories/Components/Buttons/GeneralButton/Button';
import Breadcrumb from '@stories/Components/General/Breadcrumb/Breadcrumb';
import SkipLink from '@stories/Components/General/SkipLink/SkipLink';
import qs from 'qs';
import * as React from 'react';
import { FocusRing } from 'react-aria';
import DesktopNavigation from './DesktopNavigation/DesktopNavigation';
import S from './Header.styles';
import MobileNavigation from './MobileNavigation/MobileNavigation';
const Header = (props) => {
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [searchOpen, setSearchOpen] = React.useState(false);
    const [scrollActive, setScrollActive] = React.useState(false);
    const [showBackToTop, setShowBackToTop] = React.useState(false);
    const [activeLink, setActiveLink] = React.useState('');
    const [activeSecond, setActiveSecond] = React.useState('');
    React.useEffect(() => {
        const handleScroll = () => {
            setShowSuggestions(false);
            if (window.scrollY >= 650) {
                setScrollActive(true);
                setShowBackToTop(true);
            }
            else if (window.scrollY >= 100) {
                setScrollActive(true);
                setShowBackToTop(false);
            }
            else {
                setScrollActive(false);
                setShowBackToTop(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [showSuggestions, setShowSuggestions] = React.useState(false);
    const [selectedTerm, setSelectedTerm] = React.useState('');
    const result = useApi({
        url: `${API_URLS.searchTypeahead}?${qs.stringify({ search: searchTerm })}`,
    });
    const { refContainer, serverErrors, status: formStatus, onSubmit, useReactHookFormReturn: { register, watch, setValue, formState: { errors }, }, } = useForm({
        endpoint: API_URLS.searchSubmit,
        mode: 'json',
    });
    const { search: searchValue } = watch();
    React.useEffect(() => {
        if (searchValue &&
            searchValue.length > 1 &&
            searchTerm !== searchValue &&
            searchValue !== selectedTerm) {
            setSearchTerm(searchValue);
            setShowSuggestions(true);
        }
        else {
            setShowSuggestions(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);
    return (React.createElement(React.Fragment, null,
        React.createElement(S.GlobalStyles, null),
        React.createElement(SvgClipPaths, null),
        React.createElement(SkipLink, { type: "header" }),
        React.createElement(S.Container, { "data-sticky": scrollActive && !props.hasInnerPageNav, "data-logged-in": !props.joinCta },
            React.createElement(S.TopLinksWrapper, null,
                React.createElement(S.QuickLinks, null, props.quickLinks.length > 0 &&
                    props.quickLinks.map((ql) => (React.createElement(FocusRing, { key: ql.id, focusRingClass: "focus-ring" },
                        React.createElement(S.QuickLink, { href: ql.url, target: ql.target }, ql.text))))),
                React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                    React.createElement(S.AccessibilityBtn, { className: "accessibility-activation-button" },
                        getSvgFromSystem(SystemIcon.Accessibility),
                        " ",
                        React.createElement("span", null, "Accessibility")))),
            React.createElement(S.MainWrapper, null,
                React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                    React.createElement(S.Logo, { href: "/", "aria-label": "Go to Homepage" },
                        React.createElement(SvgLogo, null))),
                React.createElement(S.MobileButtons, null,
                    React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                        React.createElement(S.MenuButton, { "data-active": menuOpen, onClick: () => {
                                if (menuOpen) {
                                    setMenuOpen(false);
                                    setActiveLink('');
                                    setActiveSecond('');
                                }
                                else {
                                    setMenuOpen(true);
                                    setSearchOpen(false);
                                }
                            } },
                            React.createElement(S.MenuIcon, null,
                                React.createElement("span", null),
                                React.createElement("span", null),
                                React.createElement("span", null),
                                React.createElement("span", null),
                                React.createElement("span", null),
                                React.createElement("span", null)),
                            "Menu")),
                    React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                        React.createElement(S.SearchButton, { "aria-label": `${searchOpen ? 'Hide' : 'Show'} Search`, "data-active": searchOpen, onClick: () => {
                                if (searchOpen) {
                                    setSearchOpen(false);
                                    setShowSuggestions(false);
                                }
                                else {
                                    setSearchOpen(true);
                                    setMenuOpen(false);
                                    setActiveLink('');
                                    setActiveSecond('');
                                }
                            } },
                            getSvgFromSystem(SystemIcon.Search),
                            getSvgFromSystem(SystemIcon.Cross)))),
                React.createElement(S.SearchCtaWrapper, { "data-open": searchOpen, "data-show-suggestions": showSuggestions && !!result.data, "data-variant": "dark", ref: refContainer },
                    React.createElement(S.SearchForm, { onSubmit: onSubmit },
                        React.createElement(S.SearchInput, { "aria-label": "Keyword search", type: "text", placeholder: props.searchPlaceholder, autoComplete: "off", ...register('search', {
                                required: 'Please enter a search term',
                                validate: (value) => validateNotWhitespaceOnly(value, 'Please enter a search term'),
                            }) }),
                        errors.search || serverErrors ? (React.createElement(S.SearchError, null,
                            errors.search && errors.search.message,
                            serverErrors.length > 0 && serverErrors.join('. '))) : null,
                        showSuggestions && !!result.data && (React.createElement(S.Autosuggest, null, result.data.suggestions.map((suggestion) => (React.createElement("li", { key: suggestion.id }, suggestion.url !== undefined ? (React.createElement(S.AutosuggestLink, { href: suggestion.url }, suggestion.value)) : (React.createElement(S.AutosuggestOption, { type: "button", onClick: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedTerm(suggestion.value);
                                setValue('search', suggestion.value);
                            } }, suggestion.value))))))),
                        React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                            React.createElement(S.SearchButton, { "aria-label": "Submit Search" }, getSvgFromSystem(SystemIcon.Search))),
                        React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                            React.createElement(S.SearchButton, { "aria-label": `${searchOpen && scrollActive ? 'Hide' : 'Show'} Search`, "data-active": searchOpen, onClick: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSearchOpen(!searchOpen);
                                    setShowSuggestions(false);
                                }, disabled: formStatus === 'pending' },
                                getSvgFromSystem(SystemIcon.Search),
                                getSvgFromSystem(SystemIcon.Cross)))),
                    props.joinCta && (React.createElement(LinkButton, { branding: "solid", href: props.joinCta.url, target: props.joinCta.target, title: props.joinCta.text ?? 'Join Us' })),
                    React.createElement(LinkButton, { branding: "outline", href: props.accountCta.url, target: props.accountCta.target, title: props.accountCta.text
                            ? props.accountCta.text
                            : props.joinCta
                                ? 'My RCR Login'
                                : 'Go to MyRCR' })),
                React.createElement(S.DesktopNavigation, { "data-search-open": searchOpen },
                    React.createElement(DesktopNavigation, { activeLink: activeLink, activeSecond: activeSecond, links: props.links, setActiveLink: setActiveLink, setActiveSecond: setActiveSecond }))),
            React.createElement(Breadcrumb, { items: props.breadcrumb, variant: props.breadcrumbVariant ?? 'light' })),
        React.createElement(MobileNavigation, { accountCta: props.accountCta, active: menuOpen, activeLink: activeLink, activeSecond: activeSecond, joinCta: props.joinCta, links: props.links, quickLinks: props.quickLinks, setActiveLink: setActiveLink, setActiveSecond: setActiveSecond }),
        React.createElement(S.BackToTop, { "data-has-cta": !!props.stickyCta, "data-mobile-only": !props.hasInnerPageNav, "data-visible": showBackToTop, onClick: () => scrollTo(0) },
            React.createElement(S.BackToTopIcon, null,
                getSvgFromSystem(SystemIcon.Chevron),
                React.createElement(S.HoverLine, null)),
            React.createElement("span", null, "Back to Top")),
        !!props.stickyCta && (React.createElement(S.StickyCta, { "data-variant": "dark" },
            React.createElement(S.StickyCtaText, null, props.stickyCta.text),
            React.createElement(LinkButton, { branding: "solid", href: props.stickyCta.link.url, title: props.stickyCta.link.text ?? '', target: props.stickyCta.link.target }))),
        React.createElement(S.BackgroundOverlay, { "data-active": activeLink !== '', onClick: () => {
                setActiveLink('');
                setActiveSecond('');
            } })));
};
export default Header;
